import { faMinus } from "@jewlr/pro-regular-svg-icons/faMinus"
import { faPlus } from "@jewlr/pro-regular-svg-icons/faPlus"
import PropTypes from "prop-types"
import { useMemo, useState } from "react"

import Button from "components/ui/button"
import Expand from "components/ui/expand"
import FontAwesomeIcon from "components/ui/font-awesome-icon"

import DescriptionMarkdown from "./description-markdown"

const HEADING_REGEX = /^(#{2,4})\s+(.+?)\s*#*\s*$/

const normalizeId = (value, index) => {
  const normalized = value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "")

  return normalized || `section-${index}`
}

const parseMarkdownSections = (markdown) => {
  const sections = []
  const intro = []
  let currentSection = null

  markdown.split("\n").forEach((line) => {
    const heading = line.match(HEADING_REGEX)

    if (heading) {
      currentSection = {
        content: [],
        headingLevel: heading[1].length,
        id: normalizeId(heading[2], sections.length),
        title: heading[2],
      }
      sections.push(currentSection)
      return
    }

    if (currentSection) {
      currentSection.content.push(line)
    } else {
      intro.push(line)
    }
  })

  return {
    intro: intro.join("\n").trim(),
    sections: sections
      .map((section) => ({
        ...section,
        content: section.content.join("\n").trim(),
      }))
      .filter((section) => section.content),
  }
}

const DescriptionAccordionItem = ({ index, section }) => {
  const [showDetails, setShowDetails] = useState(false)
  const headingId = `collection-description-heading-${section.id}-${index}`
  const contentId = `collection-description-content-${section.id}-${index}`
  const HeadingTag = `h${section.headingLevel}`

  return (
    <div>
      <HeadingTag>
        <Button
          aria-controls={contentId}
          aria-expanded={showDetails}
          className="w-full items-center justify-between gap-8 py-16 text-left"
          id={headingId}
          onClick={() => setShowDetails(!showDetails)}
        >
          <span className="text-16 desktop:text-18 font-semibold">
            {section.title}
          </span>
          <FontAwesomeIcon
            className="text-12 desktop:text-16"
            icon={showDetails ? faMinus : faPlus}
          />
        </Button>
      </HeadingTag>
      <Expand
        aria-labelledby={headingId}
        id={contentId}
        role="region"
        show={showDetails}
      >
        <div className="pb-16">
          <DescriptionMarkdown classNames={{ paragraphClassName: "mb-16" }}>
            {section.content}
          </DescriptionMarkdown>
        </div>
      </Expand>
    </div>
  )
}

DescriptionAccordionItem.propTypes = {
  index: PropTypes.number,
  section: PropTypes.shape({
    content: PropTypes.string,
    headingLevel: PropTypes.oneOf([2, 3, 4]),
    id: PropTypes.string,
    title: PropTypes.string,
  }),
}

const DescriptionAccordion = ({ children }) => {
  const { intro, sections } = useMemo(
    () => parseMarkdownSections(children || ""),
    [children]
  )

  if (!sections.length) {
    return (
      <DescriptionMarkdown classNames={{ paragraphClassName: "mb-16" }}>
        {children}
      </DescriptionMarkdown>
    )
  }

  return (
    <section aria-label="Collection Description" className="mb-24">
      {intro && (
        <div className="mb-16">
          <DescriptionMarkdown classNames={{ paragraphClassName: "mb-16" }}>
            {intro}
          </DescriptionMarkdown>
        </div>
      )}
      <div>
        {sections.map((section, index) => (
          <DescriptionAccordionItem
            index={index}
            key={`collection-description-section-${section.id}-${index}`}
            section={section}
          />
        ))}
      </div>
    </section>
  )
}

DescriptionAccordion.propTypes = {
  children: PropTypes.string,
}

export default DescriptionAccordion
